"use client";

import { AuthContext } from "@/libs/providers/AuthProvider";
import { useCallback, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";

const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const CHECK_INTERVAL = 60 * 1000; // Check every minute

export default function SessionManager({ children }) {
  const { setUserToken, setUserData, setRefreshToken } = useContext(AuthContext);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const clearSession = useCallback(() => {
    // Clear all session data
    localStorage.removeItem("userToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("lastActivityTime");

    // Clear cookies
    document.cookie = "userToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";

    // Reset context
    if (setUserToken) setUserToken(null);
    if (setUserData) setUserData(null);
    if (setRefreshToken) setRefreshToken(null);

    // Show session expired message
    toast.error("Your session has expired due to inactivity. Please log in again.");

    // Redirect to user portal
    const userPortalUrl = "https://welcome.myababil.com";
    window.location.href = userPortalUrl;
  }, [setUserToken, setUserData, setRefreshToken]);

  const updateActivityTime = useCallback(() => {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      localStorage.setItem("lastActivityTime", Date.now().toString());
    }
  }, []);

  const checkSessionTimeout = useCallback(() => {
    const lastActivityTime = localStorage.getItem("lastActivityTime");
    const userToken = localStorage.getItem("userToken");

    if (userToken && lastActivityTime) {
      const currentTime = Date.now();
      const timeDifference = currentTime - parseInt(lastActivityTime);

      if (timeDifference >= SESSION_TIMEOUT) {
        clearSession();
      }
    }
  }, [clearSession]);

  const resetSessionTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      timeoutRef.current = setTimeout(() => {
        clearSession();
      }, SESSION_TIMEOUT);
    }
  }, [clearSession]);

  // Track user activity
  useEffect(() => {
    const handleActivity = () => {
      updateActivityTime();
      resetSessionTimer();
    };

    // Events that indicate user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
      "focus",
    ];

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Initial setup
    updateActivityTime();
    resetSessionTimer();

    // Set up interval to check session timeout
    intervalRef.current = setInterval(checkSessionTimeout, CHECK_INTERVAL);

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [updateActivityTime, resetSessionTimer, checkSessionTimeout]);

  return <>{children}</>;
}
